import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';

import { readStoredValue } from './lib/storage.js';
import { applyBrandPalette, applyFavicon, getDisplayContext } from './lib/display-context.js';
import { ResolveQrPage } from './pages/ResolveQrPage.js';
import { ScanLandingPage } from './pages/ScanLandingPage.js';
import { VitrinePage } from './pages/VitrinePage.js';
import { SessionExpiredPage } from './pages/SessionExpiredPage.js';
import { MenuPage } from './pages/MenuPage.js';
import { CartPage } from './pages/CartPage.js';
import { OrdersPage } from './pages/OrdersPage.js';
import { OrderTrackPage } from './pages/OrderTrackPage.js';
import { OfflineBanner } from './components/OfflineBanner.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 },
  },
});

/** Routes reachable without a table session. */
const PUBLIC_PATHS = new Set(['/', '/scan', '/expire']);

function SessionGuard() {
  const token = readStoredValue(SESSION_TOKEN_STORAGE_KEY);
  const loc = useLocation();

  useEffect(() => {
    if (PUBLIC_PATHS.has(loc.pathname) || loc.pathname.startsWith('/t/')) return;
    if (!token) window.location.href = '/expire';
  }, [loc.pathname, token]);

  return <Outlet />;
}

export function App() {
  // Rejoue la palette de marque memorisee a chaque chargement de page : sans
  // cet effet, un rechargement (F5) retombait sur les couleurs par defaut
  // jusqu'a la prochaine resolution du QR code. Voir docs/adr/0025.
  useEffect(() => {
    const display = getDisplayContext();
    if (display) {
      applyBrandPalette(display.restaurant);
      applyFavicon(display.restaurant.logoUrl);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <OfflineBanner />
      <BrowserRouter>
        <Routes>
          <Route element={<SessionGuard />}>
            <Route path="/" element={<VitrinePage />} />
            <Route path="/scan" element={<ScanLandingPage />} />
            <Route path="/t/:qrToken" element={<ResolveQrPage />} />
            <Route path="/expire" element={<SessionExpiredPage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/panier" element={<CartPage />} />
            <Route path="/commandes" element={<OrdersPage />} />
            <Route path="/commandes/:id" element={<OrderTrackPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
